import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type {
  Client,
  ProgramType,
  Project,
  ProjectTeam,
  User,
} from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import {
  validateProjectCode,
  isProjectCodeUnique,
  normalizeProjectCode,
  validateIdentityStep,
} from './validators';
import { useNextProjectCode } from './wizard/useNextProjectCode';
import StepCodeName from './wizard/StepCodeName';
import StepClient from './wizard/StepClient';
import StepProgram from './wizard/StepProgram';
import StepTeam from './wizard/StepTeam';
import StepLinks from './wizard/StepLinks';
import StepReview from './wizard/StepReview';
import StepAvantProjet from './wizard/StepAvantProjet';
import type { ClientFormData } from '../admin/ClientWizard';
import type { TeamStepValues, LinksStepValues } from './validators';
import { validateTeam, validateLinksStep, emptyTeamValues } from './validators';
import type { AvantProjet, TeamRoleKey } from '@altyor-kronos/shared-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProjectWizardData {
  code: string;
  name: string;
  clientId: string;
  program: ProgramType;
  team: ProjectTeam;
  links: { sharepointLink: string; teamsLink: string; customerWebsite: string };
  photo: File | null;
  /** Absent = la case « avant-projet » n'a pas été cochée. */
  avantProjet?: AvantProjet;
}

export interface ProjectWizardProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProjectWizardData) => Promise<void>;
  existingProjects: readonly { id: string; code: string }[];
  clients: Array<{ id: string; data: Client }>;
  users: Array<{ id: string; data: User }>;
  /** Callback to create a client from within the wizard */
  onCreateClient: (values: ClientFormData) => Promise<string>;
  /** For edit mode */
  editing?: Project & { id: string };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_STEPS = 7;

// Step count used for navigation and progress bar

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProjectWizard({
  open,
  onClose,
  onSubmit,
  existingProjects,
  clients,
  users,
  onCreateClient,
  editing,
}: ProjectWizardProps) {
  const { t } = useTranslation();
  const suggestedCode = useNextProjectCode(existingProjects);

  // ── Step state ──────────────────────────────────────────────────────
  const [step, setStep] = useState(0);

  // ── Form state ──────────────────────────────────────────────────────
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [program, setProgram] = useState<ProgramType | null>(null);
  const [team, setTeam] = useState<TeamStepValues>(emptyTeamValues());
  const [links, setLinks] = useState<LinksStepValues>({ sharepointLink: '', teamsLink: '', customerWebsite: '' });
  const [photo, setPhoto] = useState<File | null>(null);
  // `undefined` = case décochée. La présence de la clé EST le drapeau.
  const [avantProjet, setAvantProjet] = useState<AvantProjet | undefined>(undefined);

  // ── Errors ──────────────────────────────────────────────────────────
  const [codeNameErrors, setCodeNameErrors] = useState<{ code?: string; name?: string }>({});
  const [teamErrors, setTeamErrors] = useState<{ managers?: string }>({});
  const [linksErrors, setLinksErrors] = useState<Partial<Record<keyof LinksStepValues, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  // ── Reset on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setCode(editing.code);
      setName(editing.name);
      setClientId(editing.clientId);
      setProgram(editing.currentProgram);
    } else {
      setCode('');
      setName('');
      setClientId(null);
      setProgram(null);
    }
    setStep(0);
    // Sans ce reset, un second projet créé dans la foulée hériterait en silence
    // du devis et des dates du précédent.
    setAvantProjet(undefined);
    setCodeNameErrors({});
    setSubmitting(false);
  }, [open, editing]);

  // ── Derived validation ──────────────────────────────────────────────
  const effectiveCode = code.trim() || suggestedCode;
  const codeValid = validateProjectCode(effectiveCode);
  const codeUnique = isProjectCodeUnique(
    effectiveCode,
    existingProjects,
    editing?.id,
  );

  // ── Step validation ─────────────────────────────────────────────────
  const isStepValid = useCallback(
    (s: number): boolean => {
      switch (s) {
        case 0: {
          const errs = validateIdentityStep({ code: effectiveCode, name });
          return Object.keys(errs).length === 0 && codeUnique;
        }
        case 1:
          return clientId !== null;
        case 2:
          return program !== null;
        case 3:
          return Object.keys(validateTeam(team)).length === 0;
        case 4:
          return Object.keys(validateLinksStep(links)).length === 0;
        case 5:
          return true; // Avant-projet — tout est optionnel, l'étape se franchit vide
        case 6:
          return true; // Review step — always valid
        default:
          return true;
      }
    },
    [effectiveCode, name, codeUnique, clientId, program, team, links],
  );

  // ── Navigation ──────────────────────────────────────────────────────
  const handleNext = useCallback(() => {
    // Validate current step before advancing
    if (step === 0) {
      const errs = validateIdentityStep({ code: effectiveCode, name });
      if (!codeUnique) {
        errs.code = t('projects.wizard.codeTaken');
      }
      if (Object.keys(errs).length > 0) {
        setCodeNameErrors({
          code: errs.code
            ? errs.code === 'Invalid project code'
              ? t('projects.wizard.codeInvalid')
              : errs.code
            : undefined,
          name: errs.name ? t('projects.wizard.nameRequired') : undefined,
        });
        return;
      }
      setCodeNameErrors({});
    }

    if (step === 3) {
      const errs = validateTeam(team);
      if (Object.keys(errs).length > 0) {
        setTeamErrors({ managers: errs.managers ? t('projects.wizard.managersRequired', 'At least one PM is required') : undefined });
        return;
      }
      setTeamErrors({});
    }

    if (step === 4) {
      const errs = validateLinksStep(links);
      if (Object.keys(errs).length > 0) {
        setLinksErrors(errs);
        return;
      }
      setLinksErrors({});
    }

    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
    }
  }, [step, effectiveCode, name, codeUnique, team, links, t]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        code: normalizeProjectCode(effectiveCode),
        name: name.trim(),
        clientId: clientId ?? '',
        program: program ?? 'LaunchPad',
        team,
        links,
        photo,
        avantProjet,
      });
      onClose();
    } finally {
      // Pas de catch : l'échec remonte jusqu'à ApiErrorSnackbar et l'assistant
      // reste ouvert avec la saisie (#66 : il ne faisait qu'un console.error).
      setSubmitting(false);
    }
  }, [submitting, onSubmit, effectiveCode, name, clientId, program, team, links, photo, avantProjet, onClose]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (step > 0) {
          handleBack();
        } else {
          onClose();
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        // Don't hijack Enter when focus is inside a form input in StepClient search
        const target = e.target as HTMLElement;
        const isInInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        // Allow Enter in the code/name step inputs
        if (step === 0 && isInInput) {
          e.preventDefault();
          handleNext();
          return;
        }
        // Don't hijack Enter in search fields (StepClient)
        if (isInInput) return;

        if (step === TOTAL_STEPS - 1) {
          void handleSubmit();
        } else if (isStepValid(step)) {
          handleNext();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, step, handleNext, handleBack, handleSubmit, isStepValid, onClose]);

  // ── Code/name change handler ────────────────────────────────────────
  const handleCodeNameChange = useCallback(
    (field: 'code' | 'name', value: string) => {
      if (field === 'code') setCode(value);
      else setName(value);
      // Clear relevant error on change
      if (codeNameErrors[field]) {
        setCodeNameErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [codeNameErrors],
  );

  // ── Progress ────────────────────────────────────────────────────────
  const progressValue = useMemo(
    () => (step / (TOTAL_STEPS - 1)) * 100,
    [step],
  );

  // ── Is this the final step and should we show "Create"? ─────────────
  const isFinalStep = step === TOTAL_STEPS - 1;
  const canAdvance = isStepValid(step);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (submitting) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onClose();
      }}
      fullScreen
      slotProps={{ paper: { sx: { borderRadius: 0 } } }}
    >
      {/* Yellow progress bar */}
      <LinearProgress
        variant="determinate"
        value={progressValue}
        sx={{
          height: 3,
          backgroundColor: colors.lightGray,
          '& .MuiLinearProgress-bar': {
            backgroundColor: colors.yellow,
          },
        }}
      />

      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${colors.lightGray}`,
        }}
      >
        {/* Title */}
        <Typography variant="h4">
          {editing
            ? t('projects.wizard.titleEdit')
            : t('projects.wizard.title')}
        </Typography>

        {/* Step dots */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: i === step ? 10 : 8,
                height: i === step ? 10 : 8,
                borderRadius: '50%',
                backgroundColor:
                  i === step
                    ? colors.yellow
                    : i < step
                      ? colors.bodyText
                      : colors.lightGray,
                transition: 'all 150ms',
              }}
            />
          ))}
        </Box>

        {/* Close */}
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 2, md: 4 },
          py: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 700 }}>
          <Fade in key={step} timeout={200}>
            <Box>
              {step === 0 && (
                <StepCodeName
                  code={code}
                  name={name}
                  suggestedCode={suggestedCode}
                  isCodeUnique={codeUnique}
                  isCodeValid={codeValid}
                  onChange={handleCodeNameChange}
                  errors={codeNameErrors}
                />
              )}
              {step === 1 && (
                <StepClient
                  clients={clients}
                  selectedClientId={clientId}
                  onSelect={setClientId}
                  onClientCreated={(id) => setClientId(id)}
                  onCreateClient={onCreateClient}
                />
              )}
              {step === 2 && (
                <StepProgram
                  selected={program}
                  onSelect={setProgram}
                />
              )}
              {step === 3 && (
                <StepTeam
                  team={team}
                  users={users}
                  onChange={(key: TeamRoleKey, uids: string[]) => setTeam((prev) => ({ ...prev, [key]: uids }))}
                  errors={teamErrors}
                />
              )}
              {step === 4 && (
                <StepLinks
                  links={links}
                  onChange={(field, value) => setLinks((prev) => ({ ...prev, [field]: value }))}
                  photo={photo}
                  onPhotoChange={setPhoto}
                  errors={linksErrors}
                />
              )}
              {step === 5 && (
                <StepAvantProjet
                  value={avantProjet}
                  users={users}
                  onChange={setAvantProjet}
                />
              )}
              {step === 6 && (
                <StepReview
                  code={normalizeProjectCode(effectiveCode)}
                  name={name}
                  client={clients.find((c) => c.id === clientId)?.data ?? null}
                  program={program}
                  team={team}
                  links={links}
                  photo={photo}
                  users={users}
                  onEditStep={setStep}
                />
              )}
            </Box>
          </Fade>
        </Box>
      </Box>

      {/* Bottom bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderTop: `1px solid ${colors.lightGray}`,
        }}
      >
        {/* Back button — hidden on step 0 */}
        <Box>
          {step > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleBack}
              disabled={submitting}
            >
              {t('projects.wizard.back')}
            </Button>
          )}
        </Box>

        {/* Next / Create */}
        <Button
          variant="contained"
          color="primary"
          onClick={isFinalStep ? () => void handleSubmit() : handleNext}
          disabled={submitting || (isFinalStep && !canAdvance)}
        >
          {submitting ? (
            <CircularProgress size={20} sx={{ color: colors.bodyText }} />
          ) : isFinalStep ? (
            editing
              ? t('projects.wizard.save')
              : t('projects.wizard.create')
          ) : (
            t('projects.wizard.next')
          )}
        </Button>
      </Box>
    </Dialog>
  );
}
